import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

export type FeatureLogNavFeature =
  | "materials"
  | "submittals"
  | "bid-packages"
  | "design-packages";

export type FeatureLogNavSnapshot = {
  feature: FeatureLogNavFeature;
  projectId: string;
  orderedIds: string[];
  filterModel: any | null;
  sortModel: Array<{
    colId: string;
    sort: "asc" | "desc";
    sortIndex?: number | null;
  }>;
  updatedAt: number;
};

export type FeatureLogNavContextValue = {
  getSnapshot: (
    feature: FeatureLogNavFeature,
    projectId: string
  ) => FeatureLogNavSnapshot | null;
  setSnapshot: (snapshot: FeatureLogNavSnapshot) => void;
  appendIds: (
    feature: FeatureLogNavFeature,
    projectId: string,
    ids: string[]
  ) => void;
  removeIds: (
    feature: FeatureLogNavFeature,
    projectId: string,
    ids: string[]
  ) => void;
};

const storageKey = (feature: FeatureLogNavFeature, projectId: string) =>
  `ciq:nav:${feature}:${projectId}`;

const readSnapshotFromSession = (
  feature: FeatureLogNavFeature,
  projectId: string
): FeatureLogNavSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(feature, projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.orderedIds)) return null;
    return parsed as FeatureLogNavSnapshot;
  } catch {
    return null;
  }
};

const writeSnapshotToSession = (snapshot: FeatureLogNavSnapshot) => {
  try {
    sessionStorage.setItem(
      storageKey(snapshot.feature, snapshot.projectId),
      JSON.stringify(snapshot)
    );
  } catch {
    // sessionStorage may be unavailable (e.g. private mode); silently ignore.
  }
};

export const FeatureLogNavContext = createContext<FeatureLogNavContextValue>({
  getSnapshot: () => null,
  setSnapshot: () => {},
  appendIds: () => {},
  removeIds: () => {}
});

export interface FeatureLogNavProviderProps {
  children: React.ReactNode;
}

function FeatureLogNavProvider({ children }: FeatureLogNavProviderProps) {
  const [snapshots, setSnapshots] = useState<
    Record<string, FeatureLogNavSnapshot>
  >({});
  const snapshotsRef = useRef(snapshots);
  snapshotsRef.current = snapshots;

  useEffect(() => {
    // No global hydration — snapshots are hydrated lazily per (feature, projectId)
    // on first read in case sessionStorage has surviving entries from a prior reload.
  }, []);

  const getSnapshot = useCallback(
    (
      feature: FeatureLogNavFeature,
      projectId: string
    ): FeatureLogNavSnapshot | null => {
      const key = storageKey(feature, projectId);
      const inMemory = snapshotsRef.current[key];
      if (inMemory) return inMemory;
      const fromSession = readSnapshotFromSession(feature, projectId);
      if (fromSession) {
        setSnapshots((prev) =>
          prev[key] ? prev : { ...prev, [key]: fromSession }
        );
      }
      return fromSession;
    },
    []
  );

  const setSnapshot = useCallback((snapshot: FeatureLogNavSnapshot) => {
    const key = storageKey(snapshot.feature, snapshot.projectId);
    setSnapshots((prev) => ({ ...prev, [key]: snapshot }));
    writeSnapshotToSession(snapshot);
  }, []);

  const appendIds = useCallback(
    (feature: FeatureLogNavFeature, projectId: string, ids: string[]) => {
      if (!ids?.length) return;
      const key = storageKey(feature, projectId);
      const existing =
        snapshotsRef.current[key] ??
        readSnapshotFromSession(feature, projectId);
      if (!existing) return;
      const existingSet = new Set(existing.orderedIds);
      const newIds = ids.filter((id) => !existingSet.has(id));
      if (!newIds.length) return;
      const updated: FeatureLogNavSnapshot = {
        ...existing,
        orderedIds: [...existing.orderedIds, ...newIds],
        updatedAt: Date.now()
      };
      setSnapshots((prev) => ({ ...prev, [key]: updated }));
      writeSnapshotToSession(updated);
    },
    []
  );

  const removeIds = useCallback(
    (feature: FeatureLogNavFeature, projectId: string, ids: string[]) => {
      if (!ids?.length) return;
      const key = storageKey(feature, projectId);
      const existing =
        snapshotsRef.current[key] ??
        readSnapshotFromSession(feature, projectId);
      if (!existing) return;
      const idSet = new Set(ids);
      const filtered = existing.orderedIds.filter((id) => !idSet.has(id));
      if (filtered.length === existing.orderedIds.length) return;
      const updated: FeatureLogNavSnapshot = {
        ...existing,
        orderedIds: filtered,
        updatedAt: Date.now()
      };
      setSnapshots((prev) => ({ ...prev, [key]: updated }));
      writeSnapshotToSession(updated);
    },
    []
  );

  const value = useMemo<FeatureLogNavContextValue>(
    () => ({ getSnapshot, setSnapshot, appendIds, removeIds }),
    [getSnapshot, setSnapshot, appendIds, removeIds]
  );

  return (
    <FeatureLogNavContext.Provider value={value}>
      {children}
    </FeatureLogNavContext.Provider>
  );
}

export default FeatureLogNavProvider;
